import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Select, Skeleton } from 'antd';
import { faCircleExclamation, faMagnifyingGlass, faTicket } from '@fortawesome/free-solid-svg-icons';

import { CertificateStatusEnum, ItemTypeEnum, IUserCertificate } from '@share/common-types';
import { useCertificatesWallet } from '@share/components';
import { Routes } from '@share/constants';
import { FunctionalRootState } from '@share/utils';

import { AccountEmptyState } from '../empty-state';
import { CertificatesCatalogTeaser } from './catalog-teaser';
import { AccountCertificateCard } from './certificate-card';
// Same status / product / search / sort controls as My bookings, so the two sections look identical.
import filterStyles from '../bookings/styles.module.scss';

import styles from './styles.module.scss';

// The exact statuses the header wallet drawer requests; the API rejects others (e.g. `New`).
// Module-level so the hook doesn't see a new array (and refetch) on every render.
const ALL_STATUSES: CertificateStatusEnum[] = [
  CertificateStatusEnum.Active,
  CertificateStatusEnum.Redeemed,
  CertificateStatusEnum.PartiallyRedeemed,
  CertificateStatusEnum.Expired,
  CertificateStatusEnum.Canceled,
  CertificateStatusEnum.Pending,
];

type StatusKey = 'active' | 'past' | 'cancelled' | 'all';
type ProductKey = '' | ItemTypeEnum;
type SortKey = 'purchased' | 'expiring';

// "Active" is everything still current, including certificates waiting on payment (their card says so).
const STATUSES: { key: StatusKey; labelId: string; statuses: CertificateStatusEnum[] | null }[] = [
  {
    key: 'active',
    labelId: 'my.account.hub.bookings.status.active',
    statuses: [CertificateStatusEnum.Active, CertificateStatusEnum.PartiallyRedeemed, CertificateStatusEnum.Pending, CertificateStatusEnum.New],
  },
  { key: 'past', labelId: 'my.account.hub.bookings.status.past', statuses: [CertificateStatusEnum.Redeemed, CertificateStatusEnum.Expired] },
  { key: 'cancelled', labelId: 'my.account.hub.bookings.status.cancelled', statuses: [CertificateStatusEnum.Canceled] },
  { key: 'all', labelId: 'my.account.hub.bookings.status.all', statuses: null },
];

const PRODUCTS: { key: ProductKey; labelId: string }[] = [
  { key: '', labelId: 'my.account.hub.bookings.product.all' },
  { key: ItemTypeEnum.Hotel, labelId: 'my.account.hub.bookings.product.hotels' },
  { key: ItemTypeEnum.Cruise, labelId: 'my.account.hub.certificates.product.cruises' },
  { key: ItemTypeEnum.Condo, labelId: 'my.account.hub.bookings.product.condos' },
];

const toTime = (value: string | null | undefined): number => {
  const time = Date.parse(value || '');
  return Number.isFinite(time) ? time : Number.POSITIVE_INFINITY;
};

/** Active certificates: statuses the member can still use, shared with the Overview tile. */
export const ACTIVE_CERTIFICATE_STATUSES: CertificateStatusEnum[] = [CertificateStatusEnum.Active, CertificateStatusEnum.PartiallyRedeemed];

interface IProps {
  accountUsername: string;
}

/**
 * The member's certificates inside the hub, filtered and sorted in the browser. Each card behaves like
 * the header wallet drawer's (redeem items, instalments, update payment card); see `AccountCertificateCard`.
 */
export const AccountCertificates = ({ accountUsername }: IProps): React.ReactElement => {
  const intl = useIntl();
  const user = useSelector((state: FunctionalRootState) => state.loginStore.user);

  const { certificates, loading, error, fetchCertificates } = useCertificatesWallet({
    statuses: ALL_STATUSES,
    email: user?.email,
    organizationId: user?.userAdditionalData?.organizationId,
    autoFetch: !!user?.email,
  });

  const [status, setStatus] = useState<StatusKey>('active');
  const [product, setProduct] = useState<ProductKey>('');
  const [sort, setSort] = useState<SortKey>('purchased');

  const shown = useMemo(() => {
    const statusFilter = STATUSES.find(item => item.key === status)?.statuses || null;

    return (certificates || [])
      .filter((certificate: IUserCertificate) => !statusFilter || statusFilter.includes(certificate.status))
      .filter((certificate: IUserCertificate) => !product || (certificate.items || []).some(item => item.type === product))
      .sort((a: IUserCertificate, b: IUserCertificate) => (sort === 'expiring'
        ? toTime(a.expiresAt) - toTime(b.expiresAt)
        : toTime(b.purchasedAt) - toTime(a.purchasedAt)));
  }, [certificates, status, product, sort]);

  const isFiltered = !!product;

  const catalogPath = `/${accountUsername}${Routes.Certificates}`;


  if (loading && !certificates?.length) {
    return (
      <div className={styles.certificates}>
        <div className={styles.grid}>
          {[0, 1, 2, 3].map(key => (
            <div key={key} className={styles.skeleton}><Skeleton active paragraph={{ rows: 2 }} /></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.certificates}>
        <AccountEmptyState
          variant="panel"
          icon={faCircleExclamation}
          title={intl.formatMessage({ id: 'my.account.hub.certificates.error_title' })}
          body={intl.formatMessage({ id: 'my.account.hub.certificates.error_body' })}
          action={{ label: intl.formatMessage({ id: 'my.account.hub.bookings.retry' }), onClick: fetchCertificates }}
        />
      </div>
    );
  }

  if (!certificates?.length) {
    return (
      <div className={styles.certificates}>
        <AccountEmptyState
          variant="panel"
          icon={faTicket}
          title={intl.formatMessage({ id: 'my.account.hub.certificates.empty_title' })}
          body={intl.formatMessage({ id: 'my.account.hub.certificates.empty_body' })}
          action={{ label: intl.formatMessage({ id: 'my.account.hub.certificates.browse' }), to: catalogPath }}
        />
        <CertificatesCatalogTeaser accountUsername={accountUsername} />
      </div>
    );
  }

  return (
    <div className={styles.certificates}>
      <div className={filterStyles.toolbar}>
        <div role="tablist" aria-label={intl.formatMessage({ id: 'my.account.hub.certificates.filter_label' })} className={filterStyles.segmented}>
          {STATUSES.map(item => (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={status === item.key}
              className={status === item.key ? `${filterStyles.segment} ${filterStyles.segmentActive}` : filterStyles.segment}
              onClick={() => setStatus(item.key)}
            >
              {intl.formatMessage({ id: item.labelId })}
            </button>
          ))}
        </div>
        <div className={filterStyles.controls}>
          <Select<SortKey>
            value={sort}
            onChange={setSort}
            className={filterStyles.sort}
            dropdownMatchSelectWidth={false}
            aria-label={intl.formatMessage({ id: 'my.account.hub.certificates.sort' })}
            options={[
              { value: 'purchased', label: intl.formatMessage({ id: 'my.account.hub.certificates.sort_purchased' }) },
              { value: 'expiring', label: intl.formatMessage({ id: 'my.account.hub.certificates.sort_expiring' }) },
            ]}
          />
        </div>
      </div>

      <div className={filterStyles.products} role="group" aria-label={intl.formatMessage({ id: 'my.account.hub.bookings.product_label' })}>
        <span className={filterStyles.productsLabel}>{intl.formatMessage({ id: 'my.account.hub.bookings.product_label' })}</span>
        {PRODUCTS.map(item => (
          <button
            key={item.key || 'all'}
            type="button"
            aria-pressed={product === item.key}
            className={product === item.key ? `${filterStyles.pill} ${filterStyles.pillActive}` : filterStyles.pill}
            onClick={() => setProduct(item.key)}
          >
            {intl.formatMessage({ id: item.labelId })}
          </button>
        ))}
      </div>

      <p className={filterStyles.count}>
        {intl.formatMessage({ id: 'my.account.hub.certificates.count' }, { count: shown.length })}
      </p>

      {!shown.length ? (
        <AccountEmptyState
          variant="panel"
          icon={isFiltered ? faMagnifyingGlass : faTicket}
          title={intl.formatMessage({ id: isFiltered ? 'my.account.hub.certificates.no_match_title' : 'my.account.hub.certificates.none_in_status' })}
          action={isFiltered ? {
            label: intl.formatMessage({ id: 'my.account.hub.bookings.clear_filters' }),
            onClick: () => {
              setProduct('');
            },
          } : undefined}
        />
      ) : null}

      <ul className={styles.grid}>
        {shown.map((certificate: IUserCertificate) => (
          <li key={certificate.id}>
            <AccountCertificateCard certificate={certificate} accountUsername={accountUsername} fetchCertificates={fetchCertificates} />
          </li>
        ))}
      </ul>

      <CertificatesCatalogTeaser accountUsername={accountUsername} />
    </div>
  );
};
